import React, { useState, useEffect } from 'react';
import { Search, Filter, MoreVertical, DollarSign, CreditCard, Eye, EyeOff, RefreshCw, Download } from 'lucide-react';
import { getAllPayments, processRefund } from '../../services/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showRefundModal, setShowRefundModal] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, [page, statusFilter]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const status = statusFilter !== 'all' ? statusFilter : undefined;
      const res = await getAllPayments({ page, limit: 20, status });
      setPayments(res.data.payments || res.data || []);
      setTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async (paymentId, reason, amount) => {
    try {
      await processRefund(paymentId, { reason, amount });
      toast.success('Refund processed');
      setShowRefundModal(false);
      setSelectedPayment(null);
      fetchPayments();
    } catch (error) {
      toast.error('Failed to process refund');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      paid: 'bg-green-500/20 text-green-400',
      pending: 'bg-yellow-500/20 text-yellow-400',
      refunded: 'bg-red-500/20 text-red-400',
      failed: 'bg-gray-500/20 text-gray-400'
    };
    return colors[status] || colors.pending;
  };

  return (
    <div className="space-y-6" data-testid="admin-payments">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-teko">Payment History</h2>
        <Button variant="outline" onClick={fetchPayments}>
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
        >
          <option value="all">All Status</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="refunded">Refunded</option>
        </select>
      </div>

      {/* Payments Table */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">Loading...</div>
        ) : payments.length === 0 ? (
          <div className="p-8 text-center text-[var(--text-secondary)]">No payments found</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--border)] bg-[var(--background)]">
                  <th className="text-left py-3 px-4 text-sm font-medium">User</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Amount</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Type</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium">Date</th>
                  <th className="text-right py-3 px-4 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(payment => (
                  <tr key={payment.id} className="border-b border-[var(--border)] hover:bg-[var(--background)]">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium">{payment.username || 'Unknown'}</p>
                        <p className="text-xs text-[var(--text-secondary)]">{payment.user_email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4 font-medium">
                      ${payment.amount?.toFixed(2)}
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm capitalize">{payment.type || 'subscription'}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(payment.payment_status)}`}>
                        {payment.payment_status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-[var(--text-secondary)]">
                      {new Date(payment.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 text-right">
                      {payment.payment_status === 'paid' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowRefundModal(true);
                          }}
                        >
                          Refund
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
            Previous
          </Button>
          <span className="px-4 py-2">Page {page} of {totalPages}</span>
          <Button variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
            Next
          </Button>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && selectedPayment && (
        <RefundModal
          payment={selectedPayment}
          onClose={() => {
            setShowRefundModal(false);
            setSelectedPayment(null);
          }}
          onRefund={handleRefund}
        />
      )}
    </div>
  );
}

function RefundModal({ payment, onClose, onRefund }) {
  const [reason, setReason] = useState('');
  const [amount, setAmount] = useState(payment.amount);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    await onRefund(payment.id, reason, amount);
    setProcessing(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-lg max-w-md w-full" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <h2 className="text-2xl font-teko mb-4">Process Refund</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm mb-1">Amount</label>
              <input
                type="number"
                step="0.01"
                max={payment.amount}
                value={amount}
                onChange={(e) => setAmount(parseFloat(e.target.value))}
                className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm mb-1">Reason</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full px-4 py-2 bg-[var(--background)] border border-[var(--border)] rounded-lg h-24 resize-none"
                required
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
              <Button type="submit" variant="destructive" disabled={processing} className="flex-1">
                {processing ? 'Processing...' : 'Process Refund'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AdminPayments;
